import { AppscriptsPaymentRequest } from './appscripts-payment-request.model';
import { BaseCollection } from './base-collection.model';
import { AppscriptUserFilterNetwork, NetworkCountry } from './network.model';

export enum AppscriptsNumberCommentIssue {
  MCC_MNC_ISSUE = 'MCC_MNC_ISSUE',
  ABUSE_SCAM = 'ABUSE_SCAM',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  NOT_RELIABLE = 'NOT_RELIABLE',
  OTHER = 'OTHER',
}

export const APPSCRIPTS_NUMBER_COMMENT_ISSUES = [
  {
    id: AppscriptsNumberCommentIssue.MCC_MNC_ISSUE,
    label: 'MCC/MNC issue',
  },
  {
    id: AppscriptsNumberCommentIssue.ABUSE_SCAM,
    label: 'Abuse/scam',
  },
  {
    id: AppscriptsNumberCommentIssue.NOT_SUPPORTED,
    label: 'Not supported',
  },
  {
    id: AppscriptsNumberCommentIssue.NOT_RELIABLE,
    label: 'Not reliable',
  },
  {
    id: AppscriptsNumberCommentIssue.OTHER,
    label: 'Other',
  },
];

export interface AppscriptsNumber {
  id: number;
  simId: string;
  simSlot: number;
  phoneNumber: string;
  mcc: string;
  mnc: string;
  originalMnc?: string;
  country: string;
  provider: string;
  originalProvider?: string;
  lastPing: string;
  blocked: boolean;
  updateHlr: boolean;
  comment?: string;
  commentIssue?: AppscriptsNumberCommentIssue;
  registeredAt: string;
  email: string;
  referralCode?: string;
  referrerCode?: string;
  fullName?: string | null;
  paymentMethodName?: string;
  paymentAddress?: string;
  balance: number;
  mtSmsCount: number;
  moSmsCount: number;
  voiceCallCount: number;
  mtAllowed: boolean;
  mtEnabled: boolean;
  mtDisabledAt?: string;
  moEnabled: boolean;
  moOptedIn: boolean;
  moAllowed: boolean;
  cliEnabled: boolean;
  cliOptedIn: boolean;
  cliAllowed: boolean;
  deviceToken: string;
  serviceState: string;
  simState: string;
  indexes: Index;
  userId: number;
  roaming: boolean | null;
  lastSignalStrength: number | null;
  waitingSimIdCheck: boolean | null;
}

export interface Index {
  ai: number;
  wi: number;
  phoneNumber: string;
}

export interface AppscriptsUpdateNumber {
  id?: number;
  phoneNumber: string;
  mcc: string;
  mnc: string;
  originalMnc?: string;
  blocked: boolean;
  updateHlr: boolean;
  mtEnabled?: boolean;
  mtAllowed?: boolean;
  moEnabled?: boolean;
  moOptedIn?: boolean;
  moAllowed?: boolean;
  cliEnabled?: boolean;
  cliOptedIn?: boolean;
  cliAllowed?: boolean;
  comment?: string;
  commentIssue?: AppscriptsNumberCommentIssue;
}

export interface AppscriptsUpdateUser {
  id?: number;
  email: string;
  referrerCode?: string;
  referralCode?: string;
  paymentMethodName: string;
  paymentAddress: string;
}

export interface AppscriptsNumbersCollection extends BaseCollection {
  content: AppscriptsNumber[];
}

export interface AppscriptsNumbersFilter {
  id?: string;
  phoneNumbers?: string;
  networks?: AppscriptUserFilterNetwork[];
  paymentAddress?: string;
  mcc?: string[];
  mnc?: string[];
  originalMnc?: string[];
  referrerCode?: string;
  referralCode?: string;
  email?: string;
  userId?: string;
  activeFrom?: string;
  activeTo?: string;
  mtSmsCountMin?: number;
  mtSmsCountMax?: number;
  moSmsCountMin?: number;
  moSmsCountMax?: number;
  voiceCallCountMin?: number;
  voiceCallCountMax?: number;
  balanceMin?: number;
  balanceMax?: number;
  moUserEnables?: boolean;
  mtStatuses?: ProductStatusEnum[];
  moStatuses?: ProductStatusEnum[];
  cliStatuses?: ProductStatusEnum[];
}

export interface AppscriptsNumberDetails {
  phone: string;
  mcc: string;
  mnc: string;
  originalMnc?: string;
  country: string;
  provider: string;
  originalProvider?: string;
  lastPingAt?: string | Date;

  appVersion?: string;
  androidVersion?: string;
  deviceModel?: string;

  simState?: string;
  simStateUpdatedAt?: string | Date;
  serviceState?: string;
  serviceStateUpdatedAt?: string | Date;
  roaming?: boolean;

  lastMtSmsReceivedAt?: string | Date;
  lastMoSmsReceivedAt?: string | Date;

  lastPaymentRequest?: AppscriptsPaymentRequest;
  lastMtCampaign?: MtCampaign;

  hasSimInfo: boolean;
  hasFcmToken: boolean;
  smsSendingAllowed: boolean;

  availableIndexHistory: AiHistory[];
  whiteIndexHistory: WiHistory[];
  pingHistory: PingHistory[];

  mtSmsCount: number;
  mtSmsRate: number;

  verificationInfo: VerificationInfo;
  waitingSimIdCheck: boolean;
}

export interface AiHistory {
  created_at: string | Date;
  value: number;
}

export interface WiHistory {
  created_at: string | Date;
  value: number;
}

export interface PingHistory {
  timestamp: string | Date;
  pingWeight: number;
}

export interface AppscriptsRegistrationInfo {
  register: boolean;
  deleted: boolean;
  wait: boolean;
  networks: { mcc: string; mnc: string; provider: string; country: string; is_available_registration: boolean }[];
  isPhoneNetworkAvailableForRegistration: boolean;
}

export interface AppscriptsNetworkInfo {
  id?: number;
  numberId?: number;
  mcc: string;
  mnc: string;
  name: string;
  originalMcc: string;
  originalMnc: string;
  originalName: string;
  isPorted: boolean;
  country: NetworkCountry;
  numberType: string;
}

export interface MtCampaign {
  id: number;
  createdAt: string | Date;
  phoneNumber: string;
  isChosen: boolean;
  isSentSms: boolean;
  isMtShow: boolean;
  issue?: string;
  ai: number;
  wi: number;
  isCreatedTest: boolean;
  result?: 'ok' | 'last-sms' | 'app-version' | 'bad-state' | 'not-chosen';
}

export interface VerificationInfo {
  result?: boolean;
  is_verify: boolean;
  is_verify_email: boolean;
}

export interface DeviceTokenLogsResponse {
  deviceToken: string;
  message: string;
  phones: string[];
}

export enum ProductStatusEnum {
  ENABLED = 'ENABLED',
  OPTED_IN = 'OPTED_IN',
  ALLOWED = 'ALLOWED',
  NOT_ALLOWED = 'NOT_ALLOWED',
}

/**
 * UI status truth table (flags → status)
 * Map to backend statuses
 * Note: `x` means (0 or 1).
 *
 * allowed  optedIn  enabled   →   UI status
 * -------  -------  -------       -----------
 *   0        x        x       →   Not allowed
 *   1        0        x       →   Allowed
 *   1        1        0       →   Opted in
 *   1        1        1       →   Enabled
 */
const PRODUCT_STATUSES = [
  {
    id: ProductStatusEnum.ENABLED,
    label: 'Enabled',
  },
  {
    id: ProductStatusEnum.OPTED_IN,
    label: 'Opted in',
  },
  {
    id: ProductStatusEnum.ALLOWED,
    label: 'Allowed',
  },
  {
    id: ProductStatusEnum.NOT_ALLOWED,
    label: 'Not allowed',
  },
];

export const MT_STATUSES = PRODUCT_STATUSES.filter(s => s.id !== ProductStatusEnum.ALLOWED);
export const MO_STATUSES = PRODUCT_STATUSES;
export const CLI_STATUSES = PRODUCT_STATUSES;
